import { createSelector } from 'reselect';

// Selectors
import { getGUID } from 'store/reducers/userInteractions/selectors';
import {
  getVehicle,
  getServices,
  getDeclinedServices,
  getServicePointParams,
  getTransportationOption,
  getAppointmentTime,
  getAppointmentDate,
  getTranspOptionInfo
} from 'store/reducers/appointment/selectors';

// Utils
import { isObjEmpty } from 'utils/varValidation';

const scheduleServiceProvider = 'Xtime Service Scheduler';

const getShiftServiceObject = createSelector(
  getServices,
  getDeclinedServices,
  getServicePointParams,
  (apptServices, declined, menus) => {
    const services = [];
    apptServices.forEach(service => {
      services.push({
        title: service.name,
        category: service.serviceKind,
        msrp: service.price
      });
    });
    declined.forEach(service => {
      services.push({
        title: service.name,
        category: 'repair',
        msrp: service.price
      });
    });
    if (!isObjEmpty(menus)) {
      services.push({
        title: menus.menuType.description,
        category: 'menuType',
        msrp: menus.menuType.price
      });
    }
    return services;
  }
);

export const getShiftServiceStartObj = createSelector(getGUID, guid => {
  return {
    scheduleServiceSessionId: guid,
    scheduleServiceProvider,
    events: 'scheduleServiceStart'
  };
});

export const getShiftServiceImpressionObj = createSelector(getGUID, guid => {
  return {
    scheduleServiceSessionId: guid,
    scheduleServiceProvider,
    events: 'scheduleServiceImpression'
  };
});

export const getShiftTransportShownObj = createSelector(getGUID, guid => {
  return {
    scheduleServiceSessionId: guid,
    scheduleServiceProvider,
    scheduleServiceTransportType: '',
    events: 'scheduleServiceTransportShown'
  };
});

export const getShiftTransportStartObj = createSelector(
  getGUID,
  getTransportationOption,
  getTranspOptionInfo,
  (guid, transportOption, transpOptionInfo) => {
    const transportType = transportOption
      ? transportOption
      : transpOptionInfo
        ? transpOptionInfo.data
        : '';

    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceTransportType: transportType,
      events: 'scheduleServiceTransportStart'
    };
  }
);

export const getShiftServiceLoginObj = createSelector(getGUID, guid => {
  return {
    scheduleServiceSessionId: guid,
    scheduleServiceProvider,
    events: 'scheduleServiceLogin'
  };
});

export const getShiftVehicleShownObj = createSelector(getGUID, guid => {
  return {
    scheduleServiceSessionId: guid,
    scheduleServiceProvider,
    events: 'scheduleServiceVehicleShown'
  };
});

export const getShiftVehicleStartObj = createSelector(getGUID, guid => {
  return {
    scheduleServiceSessionId: guid,
    scheduleServiceProvider,
    events: 'scheduleServiceVehicleStart'
  };
});

export const getShiftVehicleIndicatedObj = createSelector(
  getGUID,
  getVehicle,
  (guid, vehicle) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      events: 'scheduleServiceVehicleIndicated'
    };
  }
);

export const getShiftTransportFinishObj = createSelector(
  getGUID,
  getTransportationOption,
  (guid, transportOption) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceTransportType: transportOption,
      events: 'scheduleServiceTransportFinish'
    };
  }
);

export const getShiftServicesShownObj = createSelector(
  getGUID,
  getVehicle,
  (guid, vehicle) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      events: 'scheduleServiceSelectionShown'
    };
  }
);

export const getShiftServicesStartObj = createSelector(
  getGUID,
  getVehicle,
  (guid, vehicle) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      events: 'scheduleServiceSelectionStart'
    };
  }
);

export const getShiftServicesFinishObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  (guid, vehicle, services) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceServices: services,
      events: 'scheduleServiceSelectionFinish'
    };
  }
);

export const getShiftServiceApptSetObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  (guid, vehicle, services) => {
    return {
      ScheduleServiceSessionId: guid,
      scheduleServiceProvider,
      serviceVehicleYear: vehicle.year,
      serviceVehicleMake: vehicle.make,
      serviceVehicleModel: vehicle.model,
      serviceVehicleVIN: vehicle.vin,
      serviceVehicleMileage: vehicle.mileage,
      services,
      events: 'scheduleServiceApptSet'
    };
  }
);

export const getShiftServiceApptShownObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  (guid, vehicle, services) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceServices: services,
      events: 'scheduleServiceApptShown'
    };
  }
);

export const getShiftServiceApptStartObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  (guid, vehicle, services) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceServices: services,
      events: 'scheduleServiceApptStart'
    };
  }
);

export const getShiftServiceApptFinishObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  (guid, vehicle, services) => {
    return {
      ScheduleServiceSessionId: guid,
      scheduleServiceProvider,
      serviceVehicleYear: vehicle.year,
      serviceVehicleMake: vehicle.make,
      serviceVehicleModel: vehicle.model,
      serviceVehicleVIN: vehicle.vin,
      serviceVehicleMileage: vehicle.mileage,
      services,
      events: 'scheduleServiceApptFinish'
    };
  }
);

export const getShiftScheduleServiceReviewShownObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  getAppointmentDate,
  getAppointmentTime,
  (guid, vehicle, services, date, time) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceServices: services,
      scheduleServiceApptDate: date,
      scheduleServiceApptTime: time,
      events: 'scheduleServiceReviewShown'
    };
  }
);

export const getShiftScheduleServiceReviewStartObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  getAppointmentDate,
  getAppointmentTime,
  (guid, vehicle, services, date, time) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceApptDate: date,
      scheduleServiceApptTime: time,
      scheduleServiceServices: services,
      events: 'scheduleServiceReviewStart'
    };
  }
);

export const getShiftScheduleServiceReviewFinishObj = createSelector(
  getGUID,
  getVehicle,
  getShiftServiceObject,
  getAppointmentDate,
  getAppointmentTime,
  (guid, vehicle, services, date, time) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceApptDate: date,
      scheduleServiceApptTime: time,
      services,
      events: 'scheduleServiceReviewFinish'
    };
  }
);

export const getShiftScheduleServiceAccountCreatedObj = createSelector(
  getGUID,
  guid => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      events: 'scheduleServiceAccountCreated'
    };
  }
);

export const getShiftCustomerInfoFormShownObj = createSelector(
  getGUID,
  getVehicle,
  getAppointmentTime,
  getAppointmentDate,
  getShiftServiceObject,
  (guid, vehicle, appointmentTime, appointmentDate, services) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceApptDate: appointmentDate,
      scheduleServiceApptTime: appointmentTime,
      services,
      events: 'scheduleServiceFormShown'
    };
  }
);

export const getShiftCustomerInfoFormStartObj = createSelector(
  getGUID,
  getVehicle,
  getAppointmentTime,
  getAppointmentDate,
  getShiftServiceObject,
  (guid, vehicle, appointmentTime, appointmentDate, services) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceApptDate: appointmentDate,
      scheduleServiceApptTime: appointmentTime,
      scheduleServiceServices: services,
      events: 'scheduleServiceFormStart'
    };
  }
);

export const getShiftCustomerInfoFormFinishObj = createSelector(
  getGUID,
  getVehicle,
  getAppointmentTime,
  getAppointmentDate,
  getShiftServiceObject,
  (guid, vehicle, appointmentTime, appointmentDate, services) => {
    return {
      scheduleServiceSessionId: guid,
      scheduleServiceProvider,
      scheduleServiceVehicleYear: vehicle.year,
      scheduleServiceVehicleMake: vehicle.make,
      scheduleServiceVehicleModel: vehicle.model,
      scheduleServiceVehicleVin: vehicle.vin,
      scheduleServiceVehicleMileage: vehicle.mileage,
      scheduleServiceApptDate: appointmentDate,
      scheduleServiceApptTime: appointmentTime,
      scheduleServiceServices: services,
      events: 'scheduleServiceFormFinish'
    };
  }
);



// WEBPACK FOOTER //
// ./src/store/reducers/clickTracking/selectors.js